// Mock Role Service for development/testing without backend
import type {
  Role,
  RoleListItem,
  RoleRateHistory,
  RoleAuditLog,
  AuditLogQuery,
} from '@/types/role';

// Mock data
let mockRoles: Role[] = [
  {
    id: 1,
    organizationId: 1,
    name: 'Senior Developer',
    description: 'Experienced software developer with 5+ years',
    status: 'Active',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    employeeCount: 5,
    projectCount: 3,
    currentRate: {
      id: 1,
      roleId: 1,
      billingRate: 150,
      costRate: 85,
      currency: 'USD',
      effectiveFrom: '2024-01-01T00:00:00Z',
      createdAt: '2024-01-15T10:00:00Z',
      createdBy: 'admin',
      createdByName: 'Admin User',
      marginPercentage: 43.3,
      markupPercentage: 76.5,
      billingRatePerDay: 1200,
      billingRatePerMonth: 24000,
      costRatePerDay: 680,
      costRatePerMonth: 13600,
    },
  },
  {
    id: 2,
    organizationId: 1,
    name: 'Junior Developer',
    description: 'Entry-level software developer',
    status: 'Active',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    employeeCount: 8,
    projectCount: 5,
    currentRate: {
      id: 2,
      roleId: 2,
      billingRate: 80,
      costRate: 45,
      currency: 'USD',
      effectiveFrom: '2024-01-01T00:00:00Z',
      createdAt: '2024-01-15T10:00:00Z',
      createdBy: 'admin',
      createdByName: 'Admin User',
      marginPercentage: 43.8,
      markupPercentage: 77.8,
      billingRatePerDay: 640,
      billingRatePerMonth: 12800,
      costRatePerDay: 360,
      costRatePerMonth: 7200,
    },
  },
  {
    id: 3,
    organizationId: 1,
    name: 'Project Manager',
    description: 'Manages project delivery and client relationships',
    status: 'Active',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    employeeCount: 3,
    projectCount: 8,
    currentRate: {
      id: 3,
      roleId: 3,
      billingRate: 120,
      costRate: 70,
      currency: 'USD',
      effectiveFrom: '2024-01-01T00:00:00Z',
      createdAt: '2024-01-15T10:00:00Z',
      createdBy: 'admin',
      createdByName: 'Admin User',
      marginPercentage: 41.7,
      markupPercentage: 71.4,
      billingRatePerDay: 960,
      billingRatePerMonth: 19200,
      costRatePerDay: 560,
      costRatePerMonth: 11200,
    },
  },
  {
    id: 4,
    organizationId: 1,
    name: 'UX Designer',
    description: 'User experience and interface design',
    status: 'Active',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    employeeCount: 2,
    projectCount: 4,
    currentRate: {
      id: 4,
      roleId: 4,
      billingRate: 110,
      costRate: 65,
      currency: 'USD',
      effectiveFrom: '2024-01-01T00:00:00Z',
      createdAt: '2024-01-15T10:00:00Z',
      createdBy: 'admin',
      createdByName: 'Admin User',
      marginPercentage: 40.9,
      markupPercentage: 69.2,
      billingRatePerDay: 880,
      billingRatePerMonth: 17600,
      costRatePerDay: 520,
      costRatePerMonth: 10400,
    },
  },
  {
    id: 5,
    organizationId: 1,
    name: 'QA Engineer',
    description: 'Quality assurance and testing',
    status: 'Active',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    employeeCount: 4,
    projectCount: 6,
    currentRate: {
      id: 5,
      roleId: 5,
      billingRate: 90,
      costRate: 50,
      currency: 'USD',
      effectiveFrom: '2024-01-01T00:00:00Z',
      createdAt: '2024-01-15T10:00:00Z',
      createdBy: 'admin',
      createdByName: 'Admin User',
      marginPercentage: 44.4,
      markupPercentage: 80.0,
      billingRatePerDay: 720,
      billingRatePerMonth: 14400,
      costRatePerDay: 400,
      costRatePerMonth: 8000,
    },
  },
  {
    id: 6,
    organizationId: 1,
    name: 'DevOps Engineer',
    description: 'Infrastructure and deployment automation',
    status: 'Archived',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    archivedAt: '2024-11-01T10:00:00Z',
    archivedBy: 'admin',
    archivedByName: 'Admin User',
    employeeCount: 0,
    projectCount: 0,
    currentRate: {
      id: 6,
      roleId: 6,
      billingRate: 140,
      costRate: 80,
      currency: 'USD',
      effectiveFrom: '2024-01-01T00:00:00Z',
      createdAt: '2024-01-15T10:00:00Z',
      createdBy: 'admin',
      createdByName: 'Admin User',
      marginPercentage: 42.9,
      markupPercentage: 75.0,
      billingRatePerDay: 1120,
      billingRatePerMonth: 22400,
      costRatePerDay: 640,
      costRatePerMonth: 12800,
    },
  },
];

export const roleServiceMock = {
  // Role read operations
  async getAllRoles(): Promise<RoleListItem[]> {
    await delay(300);
    return mockRoles.map(toRoleListItem);
  },

  async getActiveRoles(): Promise<RoleListItem[]> {
    await delay(300);
    return mockRoles.filter(r => r.status === 'Active').map(toRoleListItem);
  },

  async getRoleById(id: number): Promise<Role> {
    await delay(300);
    const role = mockRoles.find(r => r.id === id);
    if (!role) {
      throw new Error(`Role with id ${id} not found`);
    }
    return role;
  },

  // Rate operations
  async getRateHistory(roleId: number): Promise<RoleRateHistory[]> {
    await delay(300);
    const role = mockRoles.find(r => r.id === roleId);
    if (!role) {
      throw new Error(`Role with id ${roleId} not found`);
    }

    // Mock history with 3 rates
    const rates: RoleRateHistory[] = [];
    const now = new Date();
    
    if (role.currentRate) {
      // Current rate
      rates.push({
        id: role.currentRate.id,
        billingRate: role.currentRate.billingRate,
        costRate: role.currentRate.costRate,
        currency: role.currentRate.currency,
        effectiveFrom: role.currentRate.effectiveFrom,
        effectiveTo: undefined,
        createdAt: role.currentRate.createdAt,
        createdBy: role.currentRate.createdBy,
        createdByName: role.currentRate.createdByName,
        marginPercentage: role.currentRate.marginPercentage,
        isCurrent: true,
        isFuture: false,
      });
      
      // Add historical rate
      rates.push({
        id: role.currentRate.id + 1000,
        billingRate: role.currentRate.billingRate ? role.currentRate.billingRate * 0.9 : undefined,
        costRate: role.currentRate.costRate ? role.currentRate.costRate * 0.9 : undefined,
        currency: role.currentRate.currency,
        effectiveFrom: '2023-07-01T00:00:00Z',
        effectiveTo: role.currentRate.effectiveFrom,
        createdAt: '2023-06-15T10:00:00Z',
        createdBy: 'admin',
        createdByName: 'Admin User',
        marginPercentage: calculateMargin(
          role.currentRate.billingRate ? role.currentRate.billingRate * 0.9 : undefined,
          role.currentRate.costRate ? role.currentRate.costRate * 0.9 : undefined
        ),
        isCurrent: false,
        isFuture: false,
      });
      
      // Add another historical rate
      rates.push({
        id: role.currentRate.id + 2000,
        billingRate: role.currentRate.billingRate ? role.currentRate.billingRate * 0.8 : undefined,
        costRate: role.currentRate.costRate ? role.currentRate.costRate * 0.8 : undefined,
        currency: role.currentRate.currency,
        effectiveFrom: '2023-01-01T00:00:00Z',
        effectiveTo: '2023-07-01T00:00:00Z',
        createdAt: '2022-12-15T10:00:00Z',
        createdBy: 'admin',
        createdByName: 'Admin User',
        marginPercentage: calculateMargin(
          role.currentRate.billingRate ? role.currentRate.billingRate * 0.8 : undefined,
          role.currentRate.costRate ? role.currentRate.costRate * 0.8 : undefined
        ),
        isCurrent: false,
        isFuture: false,
      });
    }

    return rates;
  },

  // Audit log operations
  async getAuditLogs(query: AuditLogQuery): Promise<RoleAuditLog[]> {
    await delay(300);
    // Return empty array for now
    return [];
  },
};

// Helper functions
function toRoleListItem(role: Role): RoleListItem {
  return {
    id: role.id,
    name: role.name,
    description: role.description,
    status: role.status,
    currentBillingRate: role.currentRate?.billingRate,
    currentCostRate: role.currentRate?.costRate,
    currency: role.currentRate?.currency,
    marginPercentage: role.currentRate?.marginPercentage,
    employeeCount: role.employeeCount,
    createdAt: role.createdAt,
  };
}

function calculateMargin(billingRate?: number, costRate?: number): number | undefined {
  if (!billingRate || !costRate || billingRate === 0) return undefined;
  return ((billingRate - costRate) / billingRate) * 100;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
